import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { PlantVariety } from '../../types/plant';
import { PlantSource } from '../../types/plantSource';
import {
  Input,
  Textarea,
  Button,
  Card,
  Alert,
  Heading,
  Text,
  Label,
  HelperText,
  InfoBox,
} from '../ui';
import { PlantVarietySearch } from './PlantVarietySearch';
import { PlantSourceSearch } from '../plantSources/PlantSourceSearch';
import { useCreatePlant } from '../../hooks/usePlantMutations';
import { usePlantSource } from '../../hooks/usePlantSourceQueries';
import { AddPlantSourceModal } from '../plantSources/AddPlantSourceModal';
import { plantSourceApi } from '../../lib/plantSourceApi';

const addPlantSchema = z.object({
  displayName: z.string().min(1, 'Display name is required').max(255, 'Display name too long'),
  plantVariety: z.custom<PlantVariety | null>().optional(),
  plantSource: z.custom<PlantSource | null>().optional(),
  notes: z.string().max(1000, 'Notes too long').optional(),
  plantedDate: z.string().optional(),
});

type AddPlantFormData = z.infer<typeof addPlantSchema>;

interface AddPlantFormProps {
  onSuccess?: (plantId: string) => void;
  onCancel?: () => void;
  defaultSourceId?: string;
}

export function AddPlantForm({ onSuccess, onCancel, defaultSourceId }: AddPlantFormProps) {
  const navigate = useNavigate();
  const createPlantMutation = useCreatePlant();

  // State modal control for Add Plant Source Modal
  const [isSourceModalOpen, setIsSourceModalOpen] = useState(false);

  // Fetch the plant source if defaultSourceId is provided
  const { data: defaultSource } = usePlantSource(defaultSourceId);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<AddPlantFormData>({
    resolver: zodResolver(addPlantSchema),
    defaultValues: {
      displayName: '',
      plantVariety: null,
      plantSource: null,
      notes: '',
      plantedDate: '',
    },
  });

  const selectedVariety = watch('plantVariety');
  const selectedSource = watch('plantSource');

  // Set the default source when it's loaded
  useEffect(() => {
    if (defaultSource && !selectedSource) {
      setValue('plantSource', defaultSource);
    }
  }, [defaultSource, selectedSource, setValue]);

  // Auto-fill display name when variety is selected
  const handleVarietyChange = (variety: PlantVariety | null) => {
    setValue('plantVariety', variety);

    // If display name is empty or matches the previous variety, update it
    const currentDisplayName = watch('displayName');
    if (
      !currentDisplayName ||
      (selectedVariety && currentDisplayName === selectedVariety.primaryCommonName)
    ) {
      setValue('displayName', variety?.primaryCommonName || '', { shouldValidate: true });
    }
  };

  // Handle successful plant source creation
  const handleSourceCreated = async (newSourceId: string) => {
    try {
      // Fetch the newly created source details
      const newSource = await plantSourceApi.getPlantSource(newSourceId);

      // Update the forms' plantSource field with the new source
      setValue('plantSource', newSource);

      // Close the modal
      setIsSourceModalOpen(false);
    } catch {
      // Still close the modal but show an error
      setIsSourceModalOpen(false);
      alert('Plant source was created but could not be selected. Please select it manually.');
    }
  };

  const onSubmit = async (data: AddPlantFormData) => {
    const plantData = {
      displayName: data.displayName,
      plantVarietyId: data.plantVariety?.id || null,
      sourceId: data.plantSource?.id || null,
      notes: data.notes || null,
      plantedDate: data.plantedDate ? new Date(data.plantedDate).toISOString() : null,
    };

    createPlantMutation.mutate(plantData, {
      onSuccess: newPlant => {
        if (onSuccess) {
          onSuccess(newPlant.id);
        } else {
          navigate(`/plants/${newPlant.id}`);
        }
      },
    });
  };

  return (
    <div>
      <Card variant="elevated">
        <div className="mb-6">
          <Heading as="h1" size="lg">
            Add New Plant{defaultSource && ` from ${defaultSource.name}`}
          </Heading>
          <Text variant="muted" className="mt-1">
            Create a new plant entry to start tracking its growth and care
          </Text>
        </div>

        {createPlantMutation.isError && (
          <Alert variant="error" className="mb-6">
            {createPlantMutation.error instanceof Error
              ? createPlantMutation.error.message
              : 'Failed to create plant'}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Plant Variety Selection */}
          <div>
            <Label>
              Plant Variety{' '}
              <Text as="span" variant="muted" size="sm">
                (optional)
              </Text>
            </Label>
            <Controller
              name="plantVariety"
              control={control}
              render={({ field }) => (
                <PlantVarietySearch
                  value={field.value}
                  onChange={handleVarietyChange}
                  error={errors.plantVariety?.message}
                  placeholder="Search for a plant variety (e.g., 'tomato', 'basil', 'rose')..."
                />
              )}
            />
            <HelperText>
              Select a plant variety for detailed information and care guides. You can also add
              plants without selecting a variety.
            </HelperText>
          </div>

          {/* Plant Source Selection */}
          <div>
            <Label>
              Plant Source{' '}
              <Text as="span" variant="muted" size="sm">
                (optional)
              </Text>
            </Label>
            <Controller
              name="plantSource"
              control={control}
              render={({ field }) => (
                <div
                  className={
                    defaultSourceId && field.value ? 'ring-2 ring-blue-200 rounded-md' : ''
                  }
                >
                  <PlantSourceSearch
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.plantSource?.message}
                    placeholder="Search for where this plant came from (e.g., nursery, seed company)..."
                    onCreateNew={() => setIsSourceModalOpen(true)}
                  />
                </div>
              )}
            />
            <HelperText>
              {defaultSourceId && selectedSource
                ? 'Auto-selected from the current page. You can change this if needed.'
                : 'Track where your plants came from to help with reordering and source reliability.'}
            </HelperText>
          </div>

          {/* Display Name */}
          <div>
            <Label htmlFor="displayName" required>
              Display Name
            </Label>
            <Input
              id="displayName"
              type="text"
              placeholder="Enter a name for this plant (e.g., 'My Tomato Plant', 'Kitchen Basil')"
              error={errors.displayName?.message}
              {...register('displayName')}
            />
            <HelperText>
              This is the name you'll see when viewing your plants. Make it descriptive and unique.
            </HelperText>
          </div>

          {/* Planted Date */}
          <div>
            <Label htmlFor="plantedDate">
              Planted Date{' '}
              <Text as="span" variant="muted" size="sm">
                (optional)
              </Text>
            </Label>
            <Input
              id="plantedDate"
              type="date"
              max={format(new Date(), 'yyyy-MM-dd')} // Can't plant in the future
              error={errors.plantedDate?.message}
              {...register('plantedDate')}
            />
            <HelperText>
              When did you plant this? This helps track the age and growth timeline.
            </HelperText>
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">
              Notes{' '}
              <Text as="span" variant="muted" size="sm">
                (optional)
              </Text>
            </Label>
            <Textarea
              id="notes"
              rows={4}
              placeholder="Add any notes about this plant (e.g., location, soil type, special care instructions)..."
              error={errors.notes?.message}
              {...register('notes')}
            />
            <HelperText>
              Optional notes about location, care instructions, or observations.
            </HelperText>
          </div>

          {/* Selected Variety Preview */}
          {selectedVariety && (
            <InfoBox variant="success">
              <div>
                <div className="font-medium text-sm mb-1">Selected Variety</div>
                <div className="text-sm">
                  <div className="font-medium">{selectedVariety.primaryCommonName}</div>
                  <div className="italic">
                    {selectedVariety.species.genus.name} {selectedVariety.species.name}
                    {selectedVariety.subspecies && ` subsp. ${selectedVariety.subspecies}`}
                    {selectedVariety.cultivarName && ` '${selectedVariety.cultivarName}'`}
                  </div>
                  {selectedVariety.plantType && (
                    <div className="mt-1">Type: {selectedVariety.plantType}</div>
                  )}
                  {selectedVariety.category && <div>Category: {selectedVariety.category}</div>}
                </div>
              </div>
            </InfoBox>
          )}

          {/* Add Plant Source Modal */}
          <AddPlantSourceModal
            isOpen={isSourceModalOpen}
            onClose={() => setIsSourceModalOpen(false)}
            onSuccess={handleSourceCreated}
          />

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              className="flex-1"
              isLoading={createPlantMutation.isPending}
              disabled={createPlantMutation.isPending}
            >
              {createPlantMutation.isPending ? 'Creating Plant...' : 'Create Plant'}
            </Button>

            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={createPlantMutation.isPending}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-gray-50 rounded-md">
          <h4 className="font-medium text-gray-900 mb-2">💡 Pro Tips</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• Use descriptive names like "Front Yard Rose Bush" or "Kitchen Window Herbs"</li>
            <li>• Adding a plant variety helps with care recommendations and identification</li>
            <li>• You can generate QR code labels after creating the plant</li>
            <li>• Notes are great for tracking location, soil amendments, or special conditions</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}
